import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, FormField, Input, Modal } from '@ntconsult/aluma-react';
import { useUpdateFolder } from '../hooks/useUpdateFolder';
import type { Folder } from '../types';

interface RenameFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  folder: Folder;
}

export function RenameFolderModal({ isOpen, onClose, folder }: RenameFolderModalProps) {
  const { t } = useTranslation(['folders', 'common']);
  const titleId = useId();
  const formId = useId();
  const [name, setName] = useState(folder.name);
  const [description, setDescription] = useState(folder.description ?? '');
  const { mutate: updateFolder, isPending } = useUpdateFolder();

  useEffect(() => {
    if (isOpen) {
      setName(folder.name);
      setDescription(folder.description ?? '');
    }
  }, [isOpen, folder.name, folder.description]);

  const trimmedName = name.trim();
  const nameChanged = trimmedName !== folder.name;
  const descriptionChanged = (description || '') !== (folder.description ?? '');
  const canSubmit = trimmedName.length > 0 && (nameChanged || descriptionChanged);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    updateFolder(
      {
        id: folder.id,
        data: {
          ...(nameChanged ? { name: trimmedName } : {}),
          ...(descriptionChanged ? { description: description || undefined } : {}),
        },
      },
      { onSuccess: () => onClose() },
    );
  };

  return (
    <Modal open={isOpen} onClose={onClose} ariaLabelledBy={titleId} size="sm">
      <Modal.Header titleId={titleId} title={t('folders:rename.title')} onClose={onClose} />
      <Modal.Body>
        <form id={formId} onSubmit={handleSubmit} className="flex flex-col gap-4">
          <FormField label={t('folders:folderName')} required>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('folders:folderNamePlaceholder')}
              autoFocus
            />
          </FormField>
          <FormField label={t('folders:description')}>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('folders:descriptionPlaceholder')}
            />
          </FormField>
        </form>
      </Modal.Body>
      <Modal.Footer>
        <Button type="button" variant="ghost" onClick={onClose} disabled={isPending}>
          {t('common:actions.cancel')}
        </Button>
        <Button
          type="submit"
          form={formId}
          variant="primary"
          loading={isPending}
          disabled={!canSubmit}
        >
          {t('folders:rename.submit')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
